type UnknownRecord = Record<string, unknown>;

type SerializedError = {
  name?: string;
  message?: string;
  stack?: string;
  code?: string;
};

type FetchBaseQueryError = {
  status: number | 'FETCH_ERROR' | 'PARSING_ERROR' | 'CUSTOM_ERROR';
  data?: unknown;
  error?: string;
};

const isRecord = (v: unknown): v is UnknownRecord =>
  typeof v === 'object' && v !== null;

const toText = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : undefined;

const isFBQError = (e: unknown): e is FetchBaseQueryError =>
  isRecord(e) && 'status' in e;

const isSerializedError = (e: unknown): e is SerializedError =>
  isRecord(e) && ('message' in e || 'name' in e || 'code' in e);

export const getMessageFromUnknown = (
  error: unknown,
  fallback = 'An unexpected error occurred'
): string => {
  if (typeof error === 'string') {return error;}
  if (error instanceof Error) {return error.message;}

  if (isFBQError(error)) {
    const data = isRecord(error.data) ? error.data : undefined;

    return (
      toText(data?.message) ??
      toText(data?.error) ??
      toText(error.error) ??
      fallback
    );
  }

  if (isSerializedError(error)) {
    return error.message ?? fallback;
  }

  if (isRecord(error)) {
    return toText(error.message) ?? toText(error.error) ?? fallback;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return fallback;
  }
};
